import { Account } from 'crono-fe-common/types/account';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import { Event, EventOutcome } from 'crono-fe-common/types/event';
import { Prospect } from 'crono-fe-common/types/prospect';
import { fullName, getProspectName } from 'crono-fe-common/utils';
import { isSameDay, parseISO } from 'date-fns';
import { PatchEventPayload } from 'hooks/services/event/usePatchEvent';
import {
  dateToTimeString,
  setTimeStringToDate,
  toUtcCalendarDay,
  toUtcMidnightOfLocalDay,
} from 'utils/dateUtils';
import * as Yup from 'yup';
import { buildAttendeeEmails, getMeetingInfo } from '../utils/meeting.helpers';

export interface MeetingFormValues {
  accountId: string | null;
  prospectId: string | null;
  opportunityId: string | null;
  searchAccountText: string;
  searchProspectText: string;
  searchOpportunityText: string;
  title: string;
  date: Date;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  description: string;
  conferenceLink: string;
  attendeeEmails: string[];
  outcome: EventOutcome | '';
  externalValues: Record<number, string>;
}

const meetingFormValidationSchema = Yup.object({
  accountId: Yup.string().nullable().required('Company is required'),
  prospectId: Yup.string().nullable().required('Contact is required'),
  title: Yup.string().required('Title is required'),
  date: Yup.date().required('Date is required'),
  isAllDay: Yup.boolean(),
  startTime: Yup.string().when('isAllDay', {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Start time is required'),
  }),
  endTime: Yup.string().when('isAllDay', {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.test(
        'end-after-start',
        'End time must be after start time',
        function (value) {
          const { startTime } = this.parent;
          if (!value || !startTime) {
            return true;
          }
          return value > startTime;
        },
      ),
  }),
  outcome: Yup.mixed<EventOutcome | ''>(),
});

// Neither the company nor the contact can be patched, so in editmode both are
// locked but must not prevent saving.
const editMeetingFormValidationSchema = meetingFormValidationSchema.shape({
  accountId: Yup.string().nullable(),
  prospectId: Yup.string().nullable(),
});

// A calendar-synced meeting only accepts a new outcome.
const outcomeOnlyMeetingFormValidationSchema = Yup.object({
  outcome: Yup.mixed<EventOutcome | ''>(),
});

interface MeetingFormValidationParams {
  isEditMode: boolean;
  isCalendarSynced: boolean;
}

export function getMeetingFormValidationSchema({
  isEditMode,
  isCalendarSynced,
}: MeetingFormValidationParams) {
  if (isCalendarSynced) {
    return outcomeOnlyMeetingFormValidationSchema;
  }

  if (isEditMode) {
    return editMeetingFormValidationSchema;
  }

  return meetingFormValidationSchema;
}

interface CreateMeetingFormInitialValues {
  initialAccount?: Account;
  initialProspect?: Prospect;
  initialOpportunity?: Opportunity;
  currentUserEmail?: string | null;
}

export function getCreateMeetingFormDefaultValues({
  initialAccount,
  initialProspect,
  initialOpportunity,
  currentUserEmail,
}: CreateMeetingFormInitialValues): MeetingFormValues {
  return {
    accountId:
      initialAccount?.objectId ??
      initialProspect?.accountId ??
      initialOpportunity?.accountId ??
      null,
    prospectId: initialProspect?.objectId ?? null,
    opportunityId: initialOpportunity?.objectId ?? null,
    searchAccountText: initialAccount?.name ?? '',
    searchProspectText:
      initialProspect?.name ??
      fullName(initialProspect?.firstName, initialProspect?.lastName) ??
      '',
    searchOpportunityText: initialOpportunity?.name ?? '',
    title: getDefaultMeetingTitle(initialProspect),
    date: new Date(),
    startTime: '',
    endTime: '',
    isAllDay: false,
    description: '',
    conferenceLink: '',
    attendeeEmails: buildAttendeeEmails(currentUserEmail, [
      initialProspect?.email,
    ]),
    outcome: EventOutcome.Scheduled,
    externalValues: {},
  };
}

interface EditMeetingFormInitialValues {
  event: Event;
}

export function getEditMeetingFormDefaultValues({
  event,
}: EditMeetingFormInitialValues): MeetingFormValues {
  const meetingInfo = getMeetingInfo(event);
  const isAllDay = meetingInfo?.isAllDay ?? false;

  const start = meetingInfo?.startTime ? parseISO(meetingInfo.startTime) : null;
  const end = meetingInfo?.endTime ? parseISO(meetingInfo.endTime) : null;

  // All-day meetings are floating calendar dates stored as UTC midnight, so
  // they are read back as their UTC day. Timed meetings are real instants,
  // read as local time.
  let date = new Date();
  if (start) {
    date = isAllDay ? toUtcCalendarDay(start) : start;
  }

  // The form holds a single day, so an end on another day (a CRM-imported span)
  // cannot be represented and is dropped.
  const hasShowableEnd = !!start && !!end && isSameDay(start, end);

  return {
    accountId: event.accountId ?? null,
    prospectId: event.prospectId ?? null,
    opportunityId: event.opportunityId ?? null,
    searchAccountText: event.account?.name ?? '',
    searchProspectText: getProspectName(getEventPrefillProspect(event)),
    searchOpportunityText: event.opportunity?.name ?? '',
    title: meetingInfo?.title ?? '',
    date,
    startTime: !isAllDay && start ? dateToTimeString(start) : '',
    endTime: !isAllDay && hasShowableEnd ? dateToTimeString(end) : '',
    isAllDay,
    description: meetingInfo?.description ?? '',
    conferenceLink: meetingInfo?.conferenceLink ?? '',
    attendeeEmails: meetingInfo?.attendeeEmails ?? [],
    // A meeting the BE never gave an outcome stays empty rather than borrowing
    // the creation default: only a field the user touches is patched, so showing
    // a value that a save would not persist would be a lie.
    outcome: event.outcome ?? '',
    externalValues: {},
  };
}

type MeetingAccountOption = Pick<Account, 'objectId' | 'name' | 'website'>;

export function getEventPrefillAccount(
  event?: Event,
): MeetingAccountOption | undefined {
  if (!event?.accountId) {
    return undefined;
  }

  return {
    objectId: event.accountId,
    name: event.account?.name ?? '',
  };
}

export function getEventPrefillProspect(event?: Event): Prospect | undefined {
  if (!event?.prospectId) {
    return undefined;
  }

  const prospect = event.prospect;

  return {
    ...prospect,
    objectId: event.prospectId,
    accountId: event.accountId ?? prospect?.accountId ?? null,
    name: prospect?.name || fullName(prospect?.firstName, prospect?.lastName),
  } as Prospect;
}

export function getEventPrefillOpportunity(
  event?: Event,
): Opportunity | undefined {
  if (!event?.opportunityId) {
    return undefined;
  }

  return {
    ...event.opportunity,
    objectId: event.opportunityId,
  } as Opportunity;
}

// Resolves the ISO span a set of form values stands for. An all-day meeting is
// a floating calendar date: it is stored as UTC midnight of the picked day with
// no end.
export function resolveMeetingSpan(values: MeetingFormValues): {
  startTime: string;
  endTime: string | null;
} {
  if (values.isAllDay) {
    return {
      startTime: toUtcMidnightOfLocalDay(values.date).toISOString(),
      endTime: null,
    };
  }

  const end = values.endTime
    ? setTimeStringToDate(values.date, values.endTime)
    : null;

  return {
    startTime: setTimeStringToDate(values.date, values.startTime).toISOString(),
    endTime: end ? end.toISOString() : null,
  };
}

export function getDefaultMeetingTitle(prospect?: Prospect | null): string {
  const name = getProspectName(prospect);
  return name ? `New meeting for ${name}` : 'New meeting';
}

// Resolves the selected account from the prefill sources or the searched list.
export function resolveSelectedAccount(
  accountId: string | null,
  initialAccount: MeetingAccountOption | undefined,
  initialProspect: Prospect | undefined,
  accounts: Account[],
): MeetingAccountOption | undefined {
  if (!accountId) {
    return undefined;
  }

  if (initialAccount && accountId === initialAccount.objectId) {
    return initialAccount;
  }

  const initialProspectAccountId =
    initialProspect?.account?.objectId ?? initialProspect?.accountId;
  if (initialProspect?.account && accountId === initialProspectAccountId) {
    return initialProspect.account;
  }

  return accounts.find((account) => account.objectId === accountId);
}

// Resolves the selected prospect from the prefill sources or the searched list.
export function resolveSelectedProspect(
  prospectId: string | null,
  initialProspect: Prospect | undefined,
  prospects: Prospect[],
): Prospect | undefined {
  if (!prospectId) {
    return undefined;
  }

  return prospectId === initialProspect?.objectId
    ? initialProspect
    : prospects.find((el) => el.objectId === prospectId);
}

// Resolves the selected deal from the prefill source or the searched list.
export function resolveSelectedOpportunity(
  opportunityId: string | null,
  initialOpportunity: Opportunity | undefined,
  opportunities: Opportunity[],
): Opportunity | undefined {
  if (!opportunityId) {
    return undefined;
  }

  return opportunityId === initialOpportunity?.objectId
    ? initialOpportunity
    : opportunities.find((el) => el.objectId === opportunityId);
}

const haveAttendeesChanged = (next: string[], initial: string[]): boolean =>
  next.length !== initial.length ||
  next.some((email) => !initial.includes(email));

interface BuildPatchEventPayloadParams {
  eventId: number;
  values: MeetingFormValues;
  initialValues: MeetingFormValues;
  isCalendarSynced: boolean;
}

// PATCH /Event is partial, so only the fields the user actually changed are sent.
export function buildPatchEventPayload({
  eventId,
  values,
  initialValues,
  isCalendarSynced,
}: BuildPatchEventPayloadParams): PatchEventPayload {
  const payload: PatchEventPayload = { eventId };

  if (values.outcome && values.outcome !== initialValues.outcome) {
    payload.outcome = values.outcome;
  }

  // For calendar-synced meetings the BE rejects a patch carrying anything but
  // the outcome.
  if (isCalendarSynced) {
    return payload;
  }

  if (values.title !== initialValues.title) {
    payload.title = values.title;
  }

  if (values.description !== initialValues.description) {
    payload.description = values.description;
  }

  if (values.conferenceLink !== initialValues.conferenceLink) {
    payload.conferenceLink = values.conferenceLink;
  }

  if (
    haveAttendeesChanged(values.attendeeEmails, initialValues.attendeeEmails)
  ) {
    payload.attendeeEmails = values.attendeeEmails;
  }

  const hasAllDayChanged = values.isAllDay !== initialValues.isAllDay;
  if (hasAllDayChanged) {
    payload.isAllDay = values.isAllDay;
  }

  // The span is only rebuilt when the user touched it: the form holds a single
  // `date`, so resending an untouched span would squash a meeting spanning
  // several days onto that day.
  const hasSpanChanged =
    hasAllDayChanged ||
    values.startTime !== initialValues.startTime ||
    values.endTime !== initialValues.endTime ||
    !isSameDay(values.date, initialValues.date);

  if (hasSpanChanged) {
    const { startTime, endTime } = resolveMeetingSpan(values);
    payload.startTime = startTime;
    payload.endTime = endTime;
  }

  // Only an end the form was actually showing can be emptied: an end on another
  // day is dropped when the form is filled, and must not read as a removal when
  // the user edits the rest of the span.
  if (initialValues.endTime && !values.endTime) {
    payload.clearEndTime = true;
  }

  return payload;
}
